import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import {
  createSpeechToTextService,
  VoiceState,
  canStartListening,
  isRecordingState,
  isErrorState,
  hasWebSpeechRecognition,
} from "../../voice";

const TRANSCRIBING_HINT = "Transcribing…";
/** Must exceed BrowserSpeechService's stop watchdog so a late final can still land. */
const STOP_UI_TIMEOUT_MS = 4500;

function withTimeout(promise, ms) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => resolve("timeout"), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

/**
 * Mic control for AI Coach: speech-to-text → insert text into input (never auto-send).
 * Chromium/Safari use the Web Speech API; other browsers use on-device Whisper.
 *
 * @param {{
 *   disabled?: boolean,
 *   languageHint?: string,
 *   onTranscript: (payload: { text: string, language: string|null, inputMode: 'voice' }) => void,
 *   primaryColor?: string,
 *   onListeningChange?: (listening: boolean, meta?: { state: string, level: number }) => void,
 *   onPartial?: (text: string) => void,
 *   onLevelChange?: (level: number) => void,
 *   autoStartToken?: number,
 *   embedded?: boolean,
 *   className?: string,
 * }} props
 */
export default function CoachVoiceButton({
  disabled = false,
  languageHint = "en",
  onTranscript,
  primaryColor = "#1976d2",
  onListeningChange = null,
  onPartial = null,
  onLevelChange = null,
  autoStartToken = 0,
  embedded = false,
  className = "",
}) {
  const [state, setState] = useState(VoiceState.IDLE);
  const [progress, setProgress] = useState(null);
  const [level, setLevel] = useState(0);
  const [hint, setHint] = useState("");
  const [privacyShown, setPrivacyShown] = useState(false);
  const [uiStuck, setUiStuck] = useState(false);
  const serviceRef = useRef(null);
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;
  const onPartialRef = useRef(onPartial);
  onPartialRef.current = onPartial;
  const onListeningChangeRef = useRef(onListeningChange);
  onListeningChangeRef.current = onListeningChange;
  const onLevelChangeRef = useRef(onLevelChange);
  onLevelChangeRef.current = onLevelChange;
  const voiceMetaRef = useRef({ state: VoiceState.IDLE, level: 0 });
  const lastAutoStartRef = useRef(0);
  const handleClickRef = useRef(null);

  useEffect(() => {
    const svc = createSpeechToTextService();
    svc.onState(setState);
    svc.onLevel((nextLevel) => {
      setLevel(nextLevel);
      onLevelChangeRef.current?.(nextLevel);
    });
    svc.onProgress(setProgress);
    svc.onError((err) => {
      setHint(errorMessage(err.code));
      setUiStuck(false);
    });
    if (typeof svc.onPartial === "function") {
      svc.onPartial((text) => {
        onPartialRef.current?.(text || "");
      });
    }
    svc.onFinal((result) => {
      const text = (result.text || "").trim();
      if (text && onTranscriptRef.current) {
        onTranscriptRef.current({
          text,
          language: result.language || null,
          inputMode: "voice",
        });
      }
      setHint("");
      setProgress(null);
      setUiStuck(false);
    });
    serviceRef.current = svc;
    return () => {
      svc.dispose();
      serviceRef.current = null;
    };
  }, []);

  const recording = isRecordingState(state);
  const loading = state === VoiceState.LOADING;
  const processing = !uiStuck && state === VoiceState.PROCESSING;
  voiceMetaRef.current = { state, level };

  // Announce start/stop only. Level ticks must not re-fire "listening",
  // or caption handlers treat every tick as a new take and wipe the transcript.
  useEffect(() => {
    const meta = voiceMetaRef.current;
    onListeningChangeRef.current?.(recording, meta);
  }, [recording]);

  const handleClick = useCallback(async () => {
    const svc = serviceRef.current;
    if (!svc || disabled) return;

    if (recording) {
      setHint(TRANSCRIBING_HINT);
      setUiStuck(false);
      try {
        const outcome = await withTimeout(svc.stopListening(), STOP_UI_TIMEOUT_MS);
        if (outcome === "timeout") {
          setUiStuck(true);
          setHint((current) => (current === TRANSCRIBING_HINT ? "Could not transcribe — try again" : current));
        }
      } catch {
        setHint((current) =>
          current === TRANSCRIBING_HINT ? "Could not transcribe — try again" : current
        );
        setUiStuck(true);
      } finally {
        setHint((current) => (current === TRANSCRIBING_HINT ? "" : current));
        setProgress(null);
      }
      return;
    }

    if (!canStartListening(state) && !isErrorState(state)) return;

    setUiStuck(false);
    if (!privacyShown) {
      setPrivacyShown(true);
      setHint(
        hasWebSpeechRecognition()
          ? "Listening…"
          : "Voice stays on your device. Only text is sent to the Coach."
      );
    } else {
      setHint("Listening…");
    }

    try {
      await svc.startListening({ languageHint });
    } catch {
      setHint((current) => (current === "Listening…" ? errorMessage(VoiceState.UNSUPPORTED_BROWSER) : current));
    }
  }, [disabled, languageHint, privacyShown, recording, state]);

  handleClickRef.current = handleClick;

  useEffect(() => {
    const token = Number(autoStartToken) || 0;
    if (!token || token === lastAutoStartRef.current) return;
    if (disabled || recording || loading || processing) return;
    lastAutoStartRef.current = token;
    const timer = window.setTimeout(() => {
      void handleClickRef.current?.();
    }, 40);
    return () => window.clearTimeout(timer);
  }, [autoStartToken, disabled, loading, processing, recording]);

  const tooltip = loading
    ? `Loading voice model… ${progress?.percent ?? 0}%`
    : processing
    ? TRANSCRIBING_HINT
    : recording
    ? "Tap to stop"
    : "Speak to Pingflo";

  return (
    <Box
      className={className}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minWidth: embedded ? 44 : 40,
      }}
    >
      <Tooltip title={tooltip}>
        <span>
          <IconButton
            onClick={handleClick}
            disabled={disabled || loading || processing}
            aria-label={recording ? "Stop listening" : "Start voice input"}
            sx={{
              width: embedded ? 48 : 40,
              height: embedded ? 48 : 40,
              color: recording ? "#fff" : primaryColor,
              bgcolor: recording
                ? "#d32f2f"
                : embedded
                  ? "rgba(255,255,255,0.08)"
                  : "transparent",
              border: embedded
                ? `1px solid ${recording ? "#ef5350" : "rgba(255,255,255,0.18)"}`
                : undefined,
              "&:hover": {
                bgcolor: recording
                  ? "#b71c1c"
                  : embedded
                    ? "rgba(255,255,255,0.14)"
                    : "rgba(25,118,210,0.08)",
              },
            }}
          >
            {loading || processing ? (
              <CircularProgress size={22} thickness={5} />
            ) : recording ? (
              <StopIcon fontSize="small" />
            ) : (
              <MicIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
      {!embedded && recording && (
        <LinearProgress
          variant="determinate"
          value={Math.round(level * 100)}
          sx={{ width: 36, height: 3, borderRadius: 1, mt: 0.25 }}
          color="error"
        />
      )}
      {!embedded && loading && progress && (
        <Typography variant="caption" sx={{ fontSize: "0.65rem", maxWidth: 72, textAlign: "center" }}>
          {progress.percent}%
        </Typography>
      )}
      {!embedded && hint && !loading && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.65rem", maxWidth: 120, textAlign: "center", lineHeight: 1.2, mt: 0.25 }}
        >
          {hint}
        </Typography>
      )}
    </Box>
  );
}

function errorMessage(code) {
  switch (code) {
    case VoiceState.PERMISSION_DENIED:
      return "Microphone permission denied";
    case VoiceState.NO_MICROPHONE:
      return "No microphone found on this device";
    case VoiceState.UNSUPPORTED_BROWSER:
      return "Voice not supported in this browser";
    case VoiceState.MODEL_LOAD_ERROR:
    case VoiceState.MODEL_DOWNLOAD_ERROR:
      return "Could not load voice model";
    case VoiceState.OUT_OF_MEMORY:
      return "Not enough memory for voice model";
    case VoiceState.TRANSCRIPTION_ERROR:
      return "Could not transcribe — try again";
    default:
      return "Voice input error";
  }
}
